import { useEffect, useMemo } from 'react';
import { CLICKFROM, STATUSTYPE, InitSelectValue } from './utils';
import type { SelectValue } from './utils';
import ResizableBox from '@/components/ResizableBox';
import api from '@/api';
import { useRequest, usePrevious, useSafeState } from 'ahooks';
import type { Part } from '@/api/epc/catalog/usageUsingPOST';
import LegendSVG from './LegendSVG';
import PartList from './PartList';
import { message } from 'antd';
import { useQuery } from '@xpeng/xdragon-ass-components-eu';
import { type VehicleInfo as VehicleInfoType } from '@/api/queryCatalog';

type LegendDetailProps = {
  modelCode: string;
  legendCode: string;
  partNo?: string;
  vin?: string;
  showPartDetail: boolean;
  showCatalog: boolean;
  vehicleInfo?: VehicleInfoType;
  onClosePartDetail: () => void;
  onOpenPartDetail: () => void;
  handlePartSearch: (partNo: string) => void;
};

const LegendDetail = (props: LegendDetailProps) => {
  const {
    modelCode,
    legendCode,
    partNo,
    vin,
    showPartDetail,
    showCatalog,
    onClosePartDetail,
    onOpenPartDetail,
    vehicleInfo
  } = props;
  const [legendSvgFileId, setLegendSvgFileId] = useSafeState<string>('');
  const [partList, setPartList] = useSafeState<Part[]>([]);
  const [validTexts, setValidTexts] = useSafeState<number[]>([]);
  const [selectValue, setSelectValue] = useSafeState<SelectValue>(InitSelectValue);
  const [defaultCallout, setDefaultCallout] = useSafeState<number>(0);
  const [legendName, setLegendName] = useSafeState<string>('');
  const previousShowPartDetail = usePrevious(showPartDetail);
  const { displayMode, directPart } = useQuery();

  const isInIframe = useMemo(() => displayMode === 'NONE', [displayMode]);

  const { loading } = useRequest(
    async () => {
      if (modelCode && legendCode) {
        const res = await api.epc.catalog.queryUsage({
          modelCode,
          legendCode,
          vin,
          directPart: Number(directPart)
        });
        if (res.code !== 200) throw new Error(res.msg);
        const _partList: Part[] = res.data?.partList || [];
        setLegendSvgFileId(res.data?.legendSvgFileId || '');
        setPartList(_partList);
        setLegendName(res.data?.legendName || '');
        const _validTexts: number[] = [];
        _partList.forEach(({ callout }) => {
          if (callout && !_validTexts.includes(callout)) _validTexts.push(callout);
        });
        setValidTexts(_validTexts);
        if (partNo) {
          const selectPart = _partList.find(item => item.partNo === partNo);
          if (selectPart && selectPart.callout && selectPart.partNo) {
            setSelectValue({
              callout: selectPart.callout,
              partNo: selectPart.partNo,
              source: CLICKFROM.PART,
              type: STATUSTYPE.HIGHLIGHT,
              isDefault: true
            });
            setDefaultCallout(selectPart.callout);
          }
        } else {
          setSelectValue({
            ...InitSelectValue
          });
          setDefaultCallout(0);
        }
      }
    },
    {
      onError: error => {
        message.error(error.message || '加载失败，请刷新重试');
        setLegendSvgFileId('');
        setPartList([]);
        setLegendName('');
        setValidTexts([]);
        setSelectValue({
          ...InitSelectValue
        });
        setDefaultCallout(0);
      },
      manual: false,
      refreshDeps: [modelCode, legendCode, partNo, vin]
    }
  );

  useEffect(() => {
    // 关闭详情时，详情态切换为高亮态
    if (previousShowPartDetail && !showPartDetail) {
      setSelectValue(pre => ({ ...pre, type: STATUSTYPE.HIGHLIGHT }));
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousShowPartDetail, showPartDetail]);

  /**
   * resize width
   */
  const baseVw = 1440;
  const [windowWidth, setWindowWidth] = useSafeState<number>(baseVw);
  useEffect(() => {
    function handleResize() {
      setWindowWidth(document.documentElement.clientWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resizeMaxWidth = useMemo(() => {
    let width = 700;
    if (showPartDetail) width = (1100 / baseVw) * windowWidth;
    else if (showCatalog) width = (700 / baseVw) * windowWidth;
    else width = (800 / baseVw) * windowWidth;
    return Math.max(700, width);
  }, [showCatalog, showPartDetail, windowWidth]);

  const resizeWidth = useMemo(() => {
    let width = 520;
    if (showPartDetail) width = (900 / baseVw) * windowWidth;
    else width = (520 / baseVw) * windowWidth;
    return Math.max(width, isInIframe ? 550 : 520);
  }, [showPartDetail, windowWidth, isInIframe]);

  return (
    <>
      <LegendSVG
        legendName={legendName}
        validTexts={validTexts}
        fileId={legendSvgFileId}
        selectValue={selectValue}
        setSelectValue={setSelectValue}
        defaultCallout={defaultCallout}
        fileIdLoading={loading}
        partList={partList}
        basicInfo={{ ...vehicleInfo, legendName }}
      />
      <ResizableBox
        direction='left'
        width={resizeWidth}
        minWidth={resizeWidth}
        maxWidth={resizeMaxWidth}
        style={{ height: '100%' }}
      >
        <PartList
          dataSource={partList}
          selectValue={selectValue}
          setSelectValue={setSelectValue}
          showPartDetail={showPartDetail}
          onClosePartDetail={onClosePartDetail}
          onOpenPartDetail={onOpenPartDetail}
          loading={loading}
          handlePartSearch={props?.handlePartSearch}
          isInIframe={isInIframe}
          vin={vin}
        />
      </ResizableBox>
    </>
  );
};

export default LegendDetail;
